import { createContext, useContext, useMemo, ReactNode } from 'react';
import { ReportsByComponentIdsQuery, useReportsByComponentIdsQuery } from '@/__generated__/graphql';

export type ComponentReportSummary =
  ReportsByComponentIdsQuery['reportsByComponentIds'][number]['reports'][number];

type ContextValue = {
  reportsByAssemblyId: Map<string, ComponentReportSummary[]>;
  loading: boolean;
};

const ComponentReportsContext = createContext<ContextValue>({
  reportsByAssemblyId: new Map(),
  loading: false,
});

type ProviderProps = {
  structureId: string;
  assemblyIds: string[];
  children: ReactNode;
};

export const ComponentReportsProvider = ({ structureId, assemblyIds, children }: ProviderProps) => {
  const uniqueAssemblyIds = useMemo(() => {
    return Array.from(new Set(assemblyIds.filter(Boolean)));
  }, [assemblyIds]);

  const { data, loading } = useReportsByComponentIdsQuery({
    variables: {
      input: {
        structureId,
        componentIds: uniqueAssemblyIds,
      },
    },
    skip: !structureId || uniqueAssemblyIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const reportsByAssemblyId = useMemo(() => {
    const map = new Map<string, ComponentReportSummary[]>();
    const groups = data?.reportsByComponentIds ?? [];
    for (const group of groups) {
      map.set(group.componentId, group.reports);
    }
    return map;
  }, [data]);

  const value = useMemo(() => ({ reportsByAssemblyId, loading }), [reportsByAssemblyId, loading]);

  return (
    <ComponentReportsContext.Provider value={value}>{children}</ComponentReportsContext.Provider>
  );
};

export const useComponentReportsContext = () => useContext(ComponentReportsContext);

export const useReportsForAssembly = (assemblyId: string | undefined) => {
  const { reportsByAssemblyId, loading } = useComponentReportsContext();
  const reports = useMemo(() => {
    if (!assemblyId) return [];
    return reportsByAssemblyId.get(assemblyId) ?? [];
  }, [reportsByAssemblyId, assemblyId]);
  return { reports, loading };
};
